import React, { useEffect, useMemo, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, User as UserIcon, BadgeCheck, Search, Check, ChevronDown, ChevronRight, Sparkles, Building2 } from "lucide-react";
import { localizeError } from "@/lib/errors";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

// Group permissions into operational categories (UX #3)
const MODULE_GROUPS = [
  {
    id: "operations",
    title_en: "Operations",
    title_ar: "العمليات",
    modules: ["dashboard", "bookings", "pos", "customers", "services", "packages"],
  },
  {
    id: "inventory",
    title_en: "Inventory",
    title_ar: "المخزون",
    modules: ["products", "categories", "purchases", "suppliers", "inventory", "stock-movements"],
  },
  {
    id: "finance",
    title_en: "Finance",
    title_ar: "المالية",
    modules: ["accounting", "accounts", "invoice-design", "reports", "commissions", "coupons"],
  },
  {
    id: "growth",
    title_en: "Growth & Marketing",
    title_ar: "النمو والتسويق",
    modules: ["website", "reviews", "analytics", "notifications", "whatsapp"],
  },
  {
    id: "admin",
    title_en: "Administration",
    title_ar: "الإدارة",
    modules: ["dashboard_executive", "users", "employees", "departments", "branches", "settings"],
  },
];

const ALL_MODULES = MODULE_GROUPS.flatMap(g => g.modules);

// Role templates (presets) — quick assignment
const ROLE_TEMPLATES = [
  {
    id: "receptionist",
    name_en: "Receptionist",
    name_ar: "موظف استقبال",
    modules: ["dashboard", "bookings", "pos", "customers", "services"],
  },
  {
    id: "stylist",
    name_en: "Stylist / Beautician",
    name_ar: "موظفة تجميل",
    modules: ["dashboard", "bookings", "customers"],
  },
  {
    id: "inventory_manager",
    name_en: "Inventory Manager",
    name_ar: "مسؤول المخزون",
    modules: ["dashboard", "products", "categories", "purchases", "suppliers", "inventory", "stock-movements"],
  },
  {
    id: "accountant",
    name_en: "Accountant",
    name_ar: "محاسب",
    modules: ["dashboard", "accounting", "accounts", "invoice-design", "reports", "commissions", "coupons"],
  },
  {
    id: "branch_manager",
    name_en: "Branch Manager",
    name_ar: "مدير فرع",
    modules: ["dashboard", "bookings", "pos", "customers", "services", "packages", "products", "inventory", "reports", "employees", "departments"],
  },
];

const empty = { username: "", password: "", name: "", role: "staff", allowed_modules: [], branch_ids: [], default_payment_method: "", default_account_id: "", employee_id: "" };

const moduleLabel = (m, t, lang) => {
  if (m === "dashboard_executive") return lang === "ar" ? "لوحة التحكم التنفيذية" : "Executive Dashboard";
  if (m === "invoice-design") return lang === "ar" ? "هوية الطباعة" : "Print Identity";
  if (m === "stock-movements") return lang === "ar" ? "حركة المخزون" : "Stock Movements";
  return t(`nav_${m.replace("-", "_")}`) || m;
};

function PermissionGroup({ group, lang, t, allowed, onToggle, onSelectAll, search }) {
  const [open, setOpen] = useState(true);
  const filtered = useMemo(() => {
    if (!search) return group.modules;
    const q = search.toLowerCase();
    return group.modules.filter(m => moduleLabel(m, t, lang).toLowerCase().includes(q));
  }, [group.modules, search, t]);

  if (filtered.length === 0) return null;

  const all = filtered.every(m => allowed.includes(m));
  const some = filtered.some(m => allowed.includes(m));

  return (
    <div className="border border-[var(--border)] rounded-md overflow-hidden bg-[var(--surface-2)]/30">
      <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--surface-2)]/60">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-2 text-sm font-medium text-[var(--text)] hover:text-[var(--gold)] transition"
          data-testid={`perm-group-toggle-${group.id}`}
        >
          {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
          {lang === "ar" ? group.title_ar : group.title_en}
          <span className="text-xs text-[var(--text-muted)] font-normal">({filtered.filter(m => allowed.includes(m)).length}/{filtered.length})</span>
        </button>
        <button
          type="button"
          onClick={() => onSelectAll(filtered, !all)}
          className="text-xs link-muted hover:text-[var(--gold)]"
          data-testid={`perm-group-selectall-${group.id}`}
        >
          {all ? (lang === "ar" ? "إلغاء الكل" : "Deselect all") : some ? (lang === "ar" ? "تحديد الكل" : "Select all") : (lang === "ar" ? "تحديد الكل" : "Select all")}
        </button>
      </div>
      {open && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 p-3">
          {filtered.map(m => {
            const active = allowed.includes(m);
            return (
              <button
                key={m}
                type="button"
                onClick={() => onToggle(m)}
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium border transition ${
                  active
                    ? "bg-[var(--gold-tint-strong)] border-[var(--gold)] text-[var(--gold)]"
                    : "bg-transparent border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--border-strong)] hover:text-[var(--text)]"
                }`}
                data-testid={`perm-${m}`}
              >
                <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition ${active ? "bg-[var(--gold)] border-[var(--gold)]" : "border-[var(--border-strong)]"}`}>
                  {active && <Check size={10} className="text-black" strokeWidth={3}/>}
                </span>
                <span className="truncate">{moduleLabel(m, t, lang)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Users() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [items, setItems] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [permSearch, setPermSearch] = useState("");

  const load = async () => {
    try {
      const [u, a, e, b, ac] = await Promise.all([
        api.get("/users"),
        api.get("/employees/available-for-user"),
        api.get("/employees"),
        api.get("/branches"),
        api.get("/accounts"),
      ]);
      setItems(u.data);
      setAvailableEmployees(a.data);
      setAllEmployees(e.data);
      setBranches(b.data || []);
      setAccounts(ac.data || []);
    } catch (err) {
      console.error("[Users] load failed:", err);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.username || !form.name || (!editId && !form.password)) {
      toast.error(t("required_field"));
      return;
    }
    try {
      if (editId) {
        const payload = {
          name: form.name,
          role: form.role,
          allowed_modules: form.allowed_modules,
          branch_ids: form.branch_ids || [],
          default_payment_method: form.default_payment_method || null,
          default_account_id: form.default_account_id || null,
          employee_id: form.employee_id || null,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editId}`, payload);
      } else {
        await api.post("/users", {
          ...form,
          branch_ids: form.branch_ids || [],
          default_payment_method: form.default_payment_method || null,
          default_account_id: form.default_account_id || null,
          employee_id: form.employee_id || null,
        });
      }
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      setOpen(false); setForm(empty); setEditId(null); setPermSearch("");
      load();
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف؟" : "Delete?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    try { await api.delete(`/users/${id}`); load(); }
    catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const toggleModule = (m) => {
    setForm(f => {
      const cur = f.allowed_modules || [];
      return { ...f, allowed_modules: cur.includes(m) ? cur.filter(x => x !== m) : [...cur, m] };
    });
  };

  const setGroupSelection = (modules, select) => {
    setForm(f => {
      const cur = new Set(f.allowed_modules || []);
      modules.forEach(m => select ? cur.add(m) : cur.delete(m));
      return { ...f, allowed_modules: Array.from(cur) };
    });
  };

  const selectAllModules = (select) => {
    setForm(f => ({ ...f, allowed_modules: select ? [...ALL_MODULES] : [] }));
  };

  const applyTemplate = (tpl) => {
    setForm(f => ({ ...f, allowed_modules: [...tpl.modules] }));
    toast.success(lang === "ar" ? `تم تطبيق قالب: ${tpl.name_ar}` : `Applied template: ${tpl.name_en}`);
  };

  const onSelectEmployee = (eid) => {
    if (!eid) {
      setForm(f => ({ ...f, employee_id: "", name: "", username: "" }));
      return;
    }
    const emp = availableEmployees.find(e => e.employee_id === eid) || allEmployees.find(e => e.employee_id === eid);
    if (emp) {
      const suggestedUser = emp.employee_code?.toLowerCase().replace(/[^a-z0-9]/g, "") || (emp.full_name || "").toLowerCase().replace(/\s+/g, ".");
      setForm(f => ({
        ...f,
        employee_id: eid,
        name: emp.full_name || f.name,
        username: editId ? f.username : suggestedUser,
      }));
    }
  };

  const empMap = allEmployees.reduce((m, e) => { m[e.employee_id] = e; return m; }, {});
  const totalAllowed = (form.allowed_modules || []).length;
  const totalModules = ALL_MODULES.length;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl gold-text">{t("nav_users")}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">{lang === "ar" ? "إدارة المستخدمين والصلاحيات" : "Manage users and permissions"}</p>
        </div>
        <button
          onClick={() => { setForm(empty); setEditId(null); setPermSearch(""); setOpen(true); }}
          data-testid="add-user-btn"
          className="btn-gold px-4 py-2 rounded-md flex items-center gap-2 text-sm"
        >
          <Plus size={16} /> {t("add")}
        </button>
      </div>

      <div className="card-surface overflow-auto">
        <table className="table-dark">
          <thead>
            <tr>
              <th>{t("username")}</th>
              <th>{t("name")}</th>
              <th>{lang === "ar" ? "الدور" : "Role"}</th>
              <th>{lang === "ar" ? "الموظف المرتبط" : "Linked Employee"}</th>
              <th>{lang === "ar" ? "الوصول" : "Access"}</th>
              <th>{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map(u => {
              const linkedEmp = u.employee_id ? empMap[u.employee_id] : null;
              return (
                <tr key={u.user_id}>
                  <td className="text-[var(--text)]"><span className="flex items-center gap-2"><UserIcon size={14} className="text-[var(--text-muted)]"/>{u.username}</span></td>
                  <td className="text-[var(--text)]">{u.name}</td>
                  <td><span className={`badge ${u.role === "admin" ? "badge-gold" : u.role === "manager" ? "badge-info" : "badge-rose"}`}>{t(`role_${u.role}`) || u.role}</span></td>
                  <td className="text-sm">
                    {linkedEmp ? (
                      <span className="flex items-center gap-1.5 text-[var(--gold)]">
                        <BadgeCheck size={14} /> {linkedEmp.full_name}
                        <span className="text-xs text-[var(--text-muted)]">({linkedEmp.employee_code})</span>
                      </span>
                    ) : (
                      <span className="text-[var(--text-disabled)]">—</span>
                    )}
                  </td>
                  <td className="text-[var(--text-muted)] text-xs">
                    {u.role === "admin" ? (
                      <span className="badge badge-gold">{lang === "ar" ? "كل الصلاحيات" : "All access"}</span>
                    ) : (
                      <span>{(u.allowed_modules || []).length} {lang === "ar" ? "وحدة" : "modules"}</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => { setForm({ ...u, password: "", allowed_modules: u.allowed_modules || [], branch_ids: u.branch_ids || [], default_payment_method: u.default_payment_method || "", default_account_id: u.default_account_id || "", employee_id: u.employee_id || "" }); setEditId(u.user_id); setPermSearch(""); setOpen(true); }}
                        data-testid={`edit-user-${u.user_id}`}
                        className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)] text-[var(--text-muted)] hover:text-[var(--gold)] transition"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => remove(u.user_id)}
                        data-testid={`delete-user-${u.user_id}`}
                        className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--error)] text-[var(--text-muted)] hover:text-[var(--error)] transition"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[var(--text-muted)] py-12">{t("no_data")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}
        >
          <div className="card-elevated p-0 w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col fade-in-up" onMouseDown={onModalPanelMouseDown}>
            <div className="px-6 pt-6 pb-4 border-b border-[var(--border-subtle)]">
              <h2 className="font-heading text-2xl gold-text">{editId ? (lang === "ar" ? "تعديل مستخدم" : "Edit User") : (lang === "ar" ? "إضافة مستخدم" : "Add User")}</h2>
              <p className="text-xs text-[var(--text-muted)] mt-1">{lang === "ar" ? "حدد بيانات الحساب والصلاحيات" : "Set account details and permissions"}</p>
            </div>

            <div className="px-6 py-5 space-y-5 overflow-auto flex-1">
              {!editId && (
                <div className="bg-[var(--surface-2)] rounded-md p-4 border border-dashed border-[var(--border)]">
                  <label className="text-xs text-[var(--text-muted)] block mb-2 font-semibold">
                    {lang === "ar" ? "ربط بموظف (يُنصح به)" : "Link to Employee (recommended)"}
                  </label>
                  <select data-testid="user-employee-select" className="input-dark" value={form.employee_id} onChange={e => onSelectEmployee(e.target.value)}>
                    <option value="">{lang === "ar" ? "— بدون ربط —" : "— None —"}</option>
                    {availableEmployees.map(e => (
                      <option key={e.employee_id} value={e.employee_id}>{e.employee_code} — {e.full_name} {e.job_title ? `(${e.job_title})` : ""}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-[var(--text-muted)] mt-2">
                    {availableEmployees.length === 0
                      ? (lang === "ar" ? "لا يوجد موظفون بدون حسابات. أضف موظفًا أولاً." : "No employees without accounts. Add one first.")
                      : (lang === "ar" ? `${availableEmployees.length} موظف متاح للربط.` : `${availableEmployees.length} employees available.`)}
                  </p>
                </div>
              )}

              {editId && form.employee_id && empMap[form.employee_id] && (
                <div className="bg-[var(--gold-tint)] rounded-md p-3 border border-[var(--gold)]/30 flex items-center gap-2 text-sm">
                  <BadgeCheck size={18} className="text-[var(--gold)]" />
                  <span className="text-[var(--text)]">{lang === "ar" ? "مرتبط بالموظف:" : "Linked to:"} <strong>{empMap[form.employee_id].full_name}</strong> ({empMap[form.employee_id].employee_code})</span>
                </div>
              )}

              {/* Account fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">{t("username")}</label>
                  <input className="input-dark" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} disabled={!!editId} data-testid="user-username" />
                </div>
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">{t("name")}</label>
                  <input className="input-dark" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
                </div>
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">{t("password")} {editId && <span className="text-[var(--text-disabled)]">({lang === "ar" ? "اختياري" : "optional"})</span>}</label>
                  <input type="password" className="input-dark" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
                </div>
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">{lang === "ar" ? "الدور" : "Role"}</label>
                  <select className="input-dark" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} data-testid="user-role">
                    <option value="admin">{t("role_admin")}</option>
                    <option value="manager">{t("role_manager")}</option>
                    <option value="staff">{t("role_staff")}</option>
                  </select>
                </div>
              </div>

              {/* Permissions section */}
              {form.role !== "admin" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
                    <div>
                      <h3 className="text-sm font-semibold text-[var(--text)]">{lang === "ar" ? "الصلاحيات" : "Permissions"}</h3>
                      <p className="text-xs text-[var(--text-muted)] mt-0.5">{totalAllowed} / {totalModules} {lang === "ar" ? "وحدة مفعّلة" : "modules enabled"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => selectAllModules(true)} className="btn-ghost text-xs px-2.5 py-1 rounded">{lang === "ar" ? "تحديد الكل" : "Select all"}</button>
                      <button type="button" onClick={() => selectAllModules(false)} className="btn-ghost text-xs px-2.5 py-1 rounded">{lang === "ar" ? "مسح الكل" : "Clear all"}</button>
                    </div>
                  </div>

                  {/* Role templates */}
                  <div className="bg-[var(--surface-2)]/40 rounded-md p-3 border border-[var(--border-subtle)]">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles size={12} className="text-[var(--gold)]"/>
                      <span className="text-xs font-semibold text-[var(--text)]">{lang === "ar" ? "قوالب جاهزة" : "Quick Templates"}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {ROLE_TEMPLATES.map(tpl => (
                        <button
                          key={tpl.id}
                          type="button"
                          onClick={() => applyTemplate(tpl)}
                          className="text-xs px-2.5 py-1 rounded-full border border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition"
                          data-testid={`perm-template-${tpl.id}`}
                        >
                          {lang === "ar" ? tpl.name_ar : tpl.name_en}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Search */}
                  <div className="relative">
                    <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-[var(--text-muted)] pointer-events-none"/>
                    <input
                      type="text"
                      className="input-dark ps-9"
                      placeholder={lang === "ar" ? "ابحث عن صلاحية..." : "Search permission..."}
                      value={permSearch}
                      onChange={e => setPermSearch(e.target.value)}
                      data-testid="perm-search"
                    />
                  </div>

                  {/* Grouped modules */}
                  <div className="space-y-2">
                    {MODULE_GROUPS.map(g => (
                      <PermissionGroup
                        key={g.id}
                        group={g}
                        lang={lang}
                        t={t}
                        allowed={form.allowed_modules || []}
                        onToggle={toggleModule}
                        onSelectAll={setGroupSelection}
                        search={permSearch}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Branches scope (admin role bypasses branch restriction implicitly) */}
              {form.role !== "admin" && branches.length > 1 && (
                <div className="bg-[var(--surface-2)]/40 rounded-md p-4 border border-[var(--border)]" data-testid="user-branches-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 size={14} className="text-[var(--gold)]"/>
                    <span className="text-sm font-semibold text-[var(--text)]">{lang === "ar" ? "الفروع المسموحة" : "Allowed Branches"}</span>
                  </div>
                  <p className="text-[11px] text-[var(--text-muted)] mb-3">
                    {lang === "ar"
                      ? "اترك فارغًا للسماح بكل الفروع. اختر فرعًا أو أكثر لتقييد المستخدم بهذه الفروع فقط."
                      : "Leave empty for all branches. Select one or more to restrict the user to those branches only."}
                  </p>
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, branch_ids: [] }))}
                      className={`text-xs px-3 py-1 rounded-full border transition ${(form.branch_ids || []).length === 0 ? "bg-[var(--gold)] text-black border-[var(--gold)]" : "border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--gold)] hover:text-[var(--gold)]"}`}
                      data-testid="branch-scope-all-btn"
                    >
                      {lang === "ar" ? "كل الفروع" : "All branches"}
                    </button>
                    <span className="text-[10px] text-[var(--text-muted)]">
                      {(form.branch_ids || []).length === 0
                        ? (lang === "ar" ? "وصول كامل" : "full access")
                        : `${(form.branch_ids || []).length} ${lang === "ar" ? "فرع محدد" : "selected"}`}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {branches.map(b => {
                      const checked = (form.branch_ids || []).includes(b.branch_id);
                      const name = lang === "ar" ? (b.name_ar || b.name_en) : (b.name_en || b.name_ar);
                      return (
                        <button
                          key={b.branch_id}
                          type="button"
                          onClick={() => setForm(f => {
                            const cur = new Set(f.branch_ids || []);
                            if (cur.has(b.branch_id)) cur.delete(b.branch_id); else cur.add(b.branch_id);
                            return { ...f, branch_ids: Array.from(cur) };
                          })}
                          data-testid={`user-branch-${b.branch_id}`}
                          className={`text-xs px-2.5 py-1 rounded-full border flex items-center gap-1.5 transition ${
                            checked
                              ? "bg-[var(--gold-tint)] border-[var(--gold)] text-[var(--gold)]"
                              : "border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--gold)]"
                          }`}
                        >
                          {checked && <Check size={11}/>}
                          {name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Default POS payment + account */}
              <div className="bg-[var(--surface-2)]/40 rounded-md p-4 border border-[var(--border)]" data-testid="user-defaults-section">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={14} className="text-[var(--gold)]"/>
                  <span className="text-sm font-semibold text-[var(--text)]">{lang === "ar" ? "افتراضيات نقطة البيع (POS)" : "POS Defaults"}</span>
                </div>
                <p className="text-[11px] text-[var(--text-muted)] mb-3">
                  {lang === "ar"
                    ? "اختياري — إذا حُدّد، يتم اختيار طريقة الدفع والصندوق تلقائياً عند فتح POS. تركها فارغاً يعني أن المستخدم سيختار يدوياً (إلزامي عند الدفع)."
                    : "Optional — if set, the payment method and account will be pre-selected at POS. Leaving them empty keeps manual selection (required at checkout)."}
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[var(--text-muted)] mb-1 block">{lang === "ar" ? "طريقة الدفع الافتراضية" : "Default payment method"}</label>
                    <select
                      className="input-dark w-full"
                      value={form.default_payment_method || ""}
                      onChange={(e) => setForm({ ...form, default_payment_method: e.target.value })}
                      data-testid="user-default-payment-method"
                    >
                      <option value="">{lang === "ar" ? "— بدون افتراضي —" : "— No default —"}</option>
                      <option value="cash">{lang === "ar" ? "نقدي" : "Cash"}</option>
                      <option value="card">{lang === "ar" ? "بطاقة" : "Card"}</option>
                      <option value="transfer">{lang === "ar" ? "تحويل" : "Transfer"}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[var(--text-muted)] mb-1 block">{lang === "ar" ? "الصندوق/البنك الافتراضي" : "Default cash/bank account"}</label>
                    <select
                      className="input-dark w-full"
                      value={form.default_account_id || ""}
                      onChange={(e) => setForm({ ...form, default_account_id: e.target.value })}
                      data-testid="user-default-account"
                    >
                      <option value="">{lang === "ar" ? "— بدون افتراضي —" : "— No default —"}</option>
                      {accounts.map(a => (
                        <option key={a.account_id} value={a.account_id}>
                          {(lang === "ar" ? a.name_ar : a.name_en) || a.name_en || a.account_id}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {form.role === "admin" && (
                <div className="bg-[var(--gold-tint)] border border-[var(--gold)]/30 rounded-md p-4 flex items-center gap-3">
                  <BadgeCheck size={20} className="text-[var(--gold)]"/>
                  <div className="text-sm text-[var(--text)]">{lang === "ar" ? "المسؤول لديه وصول كامل إلى جميع الوحدات" : "Admin has full access to all modules"}</div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-[var(--border-subtle)] flex gap-3 justify-end bg-[var(--surface-2)]/30">
              <button className="btn-ghost px-4 py-2 rounded-md text-sm" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md text-sm" data-testid="user-save" onClick={submit}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
